/**
 * CompetitionFormModal — создание/редактирование соревнования.
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, Stack, TextInput, Textarea, Select, MultiSelect, Switch, Button, Group,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Черновик' },
  { value: 'registration_open', label: 'Регистрация открыта' },
  { value: 'registration_closed', label: 'Регистрация закрыта' },
  { value: 'in_progress', label: 'Идёт' },
  { value: 'completed', label: 'Завершено' },
];

const CompetitionFormModal = ({ opened, onClose, onSuccess, competition = null, subjects = [] }) => {
  const isEdit = !!competition;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_date: null,
    registration_deadline: null,
    status: 'draft',
    is_active: false,
    subject_ids: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (competition) {
      setFormData({
        name: competition.name || '',
        description: competition.description || '',
        event_date: competition.event_date ? new Date(competition.event_date) : null,
        registration_deadline: competition.registration_deadline ? new Date(competition.registration_deadline) : null,
        status: competition.status || 'draft',
        is_active: competition.is_active || false,
        subject_ids: (competition.subjects || []).map((s) => s.id),
      });
    } else {
      setFormData({
        name: '',
        description: '',
        event_date: null,
        registration_deadline: null,
        status: 'draft',
        is_active: false,
        subject_ids: [],
      });
    }
  }, [competition, opened]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      notifications.show({ title: 'Ошибка', message: 'Введите название', color: 'red' });
      return;
    }
    if (!formData.event_date) {
      notifications.show({ title: 'Ошибка', message: 'Выберите дату проведения', color: 'red' });
      return;
    }

    setLoading(true);
    try {
      const toDateStr = (d) => {
        if (!d) return null;
        if (typeof d === 'string') return d.split('T')[0];
        if (d instanceof Date) return d.toISOString().split('T')[0];
        return String(d);
      };

      const payload = {
        name: formData.name,
        description: formData.description || null,
        event_date: toDateStr(formData.event_date),
        registration_deadline: toDateStr(formData.registration_deadline),
        status: formData.status,
        is_active: formData.is_active,
      };
      if (!isEdit) {
        payload.subject_ids = formData.subject_ids;
      }

      const url = isEdit ? `/api/competitions/${competition.id}` : '/api/competitions';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка сохранения');
      }

      // Синхронизация предметов при редактировании
      if (isEdit) {
        const oldIds = (competition.subjects || []).map((s) => s.id);
        const newIds = formData.subject_ids;
        const toAdd = newIds.filter((id) => !oldIds.includes(id));
        const toRemove = oldIds.filter((id) => !newIds.includes(id));

        for (const sid of toAdd) {
          await fetch(`/api/competitions/${competition.id}/subjects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ subject_id: sid }),
          });
        }
        for (const sid of toRemove) {
          await fetch(`/api/competitions/${competition.id}/subjects/${sid}`, {
            method: 'DELETE',
            credentials: 'include',
          });
        }
      }
      notifications.show({
        title: 'Успешно',
        message: isEdit ? 'Соревнование обновлено' : 'Соревнование создано',
        color: 'green',
      });
      onSuccess();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const subjectOptions = subjects.map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактирование соревнования' : 'Новое соревнование'}
      centered
      radius="lg"
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Название"
          placeholder="Зимний турнир 2026"
          value={formData.name}
          onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
          required
          radius="xl"
        />

        <Textarea
          label="Описание"
          placeholder="Описание соревнования"
          value={formData.description}
          onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
          radius="xl"
          autosize
          minRows={2}
        />

        <Group grow>
          <DatePickerInput
            label="Дата проведения"
            placeholder="Выберите дату"
            value={formData.event_date}
            onChange={(val) => setFormData((p) => ({ ...p, event_date: val }))}
            locale="ru"
            required
            radius="xl"
          />
          <DatePickerInput
            label="Дедлайн записи"
            placeholder="Необязательно"
            value={formData.registration_deadline}
            onChange={(val) => setFormData((p) => ({ ...p, registration_deadline: val }))}
            locale="ru"
            clearable
            radius="xl"
          />
        </Group>

        <Select
          label="Статус"
          data={STATUS_OPTIONS}
          value={formData.status}
          onChange={(val) => setFormData((p) => ({ ...p, status: val }))}
          radius="xl"
        />

        <MultiSelect
          label="Предметы"
          placeholder="Выберите предметы"
          data={subjectOptions}
          value={formData.subject_ids.map(String)}
          onChange={(vals) => setFormData((p) => ({ ...p, subject_ids: (vals || []).map(Number) }))}
          searchable
          clearable
          radius="xl"
        />

        <Switch
          label="Активно в ЛК родителя (показывать баннер)"
          checked={!!formData.is_active}
          onChange={(e) => setFormData((p) => ({ ...p, is_active: e?.currentTarget?.checked ?? false }))}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Отмена</Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CompetitionFormModal;
